import { useFormikContext } from "formik";
import { getDefaultBasicPlugin, getInputStoryBibleBlock } from "../../../../../../../src/shared/schemas/PluginSchema";
import { WORKFLOW_BLOCK_TYPE } from "../../../../../../../src/shared/types/workflows";
import { SudoButton } from "src/sudokit/components/SudoButton";
import BlockEditor from "./BlockEditor";
import { generateId } from "../../../../../../../src/shared/schemas/CharacterSchema";
import OutputEditor from "./OutputEditor";
import GeneralBaseModal from "src/components/Modals/GeneralBaseModal";
import { useState } from "react";

const defaultPlugin = getDefaultBasicPlugin();
const defaultInferenceBlock = defaultPlugin.blocks.find((b) => b.blockType === "inference");

const inputBlockTypes = [
  WORKFLOW_BLOCK_TYPE.FEATURE_FLAGS,
  WORKFLOW_BLOCK_TYPE.API_INPUT,
  WORKFLOW_BLOCK_TYPE.INPUT_DOCUMENT,
  WORKFLOW_BLOCK_TYPE.INPUT_STORY_BIBLE,
  WORKFLOW_BLOCK_TYPE.AB_TEST,
  WORKFLOW_BLOCK_TYPE.COST_CONTROLS,
  WORKFLOW_BLOCK_TYPE.POV_AND_TENSE,
];
const outputBlockTypes = [WORKFLOW_BLOCK_TYPE.CARD_OUTPUT, WORKFLOW_BLOCK_TYPE.CLOSE_REQUEST];

export default function AdvancedEditor({ workflow }) {
  const { values, setFieldValue } = useFormikContext();
  const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");

  const inputBlocks = values.blocks.filter((b) => inputBlockTypes.includes(b.blockType));
  const hasStoryBible = inputBlocks.some((b) => b.blockType === WORKFLOW_BLOCK_TYPE.INPUT_STORY_BIBLE);
  const inferenceBlocks = values.blocks.filter((b) => b.blockType === WORKFLOW_BLOCK_TYPE.INFERENCE);
  const hasFeatureFlags = values.blocks.some((b) => b.blockType === WORKFLOW_BLOCK_TYPE.FEATURE_FLAGS);
  const hasPOVAndTense = values.blocks.some((b) => b.blockType === WORKFLOW_BLOCK_TYPE.POV_AND_TENSE);
  const outputBlocks = values.blocks.filter((b) => outputBlockTypes.includes(b.blockType));
  const latestSystemPrompt = inferenceBlocks[inferenceBlocks.length - 1]?.options?.prompt?.messages[0]?.template;

  const addPromptStage = (e) => {
    e.preventDefault();

    // create the block.
    const newBlock = {
      id: `prompt-${generateId()}`,
      blockType: WORKFLOW_BLOCK_TYPE.INFERENCE,
      options: {
        ...defaultInferenceBlock.options,
        prompt: {
          type: "chat",
          messages: [
            {
              role: "system",
              template: latestSystemPrompt,
            },
            {
              role: "user",
              template: "",
            },
          ],
        },
      },
      inputs: [],
    };

    const newBlocks = values.blocks.reduce((acc, block, blockIndex, currentBlocks) => {
      if (block.blockType === WORKFLOW_BLOCK_TYPE.INFERENCE) {
        // all existing inference blocks must not stream and can only generate one result.
        const noStreamSingleGeneration = {
          ...block,
          options: {
            ...block.options,
            stream: false,
            numGenerations: 1,
            outputName: block.options.outputName || `output_${block.id}`,
          },
        };

        // inference blocks are all tegether and the new one must be added to the end.
        if (currentBlocks[blockIndex + 1]?.blockType === WORKFLOW_BLOCK_TYPE.INFERENCE) {
          return [...acc, noStreamSingleGeneration];
        }
        return [...acc, noStreamSingleGeneration, newBlock];
      }
      return [...acc, block];
    }, []);

    setFieldValue("blocks", newBlocks);
  };

  const enableStoryBible = (e) => {
    e.preventDefault();
    const hasStoryBible = values.blocks.some((b) => b.blockType === WORKFLOW_BLOCK_TYPE.INPUT_STORY_BIBLE);

    if (hasStoryBible) return;
    const firstInferenceIndex = values.blocks.findIndex((b) => b.blockType === WORKFLOW_BLOCK_TYPE.INFERENCE);
    const newBlocks = [
      ...values.blocks.slice(0, firstInferenceIndex),
      {
        ...getInputStoryBibleBlock(),
        id: `story-bible-1`,
      },
      ...values.blocks.slice(firstInferenceIndex),
    ];
    setFieldValue("blocks", newBlocks);
  };

  const addAbTest = (e) => {
    e.preventDefault();
    const newBlocks = [
      {
        id: `ab-test-1`,
        blockType: WORKFLOW_BLOCK_TYPE.AB_TEST,
        options: {
          experimentName: "",
          population: "all",
          skipFlaggedContent: false,
          assignmentStrategy: "request",
          percentage: 100,
          controlVariationName: "",
          testVariationName: "",
        },
      },
      ...values.blocks,
    ];
    setFieldValue("blocks", newBlocks);
  };

  const addCostControls = (e) => {
    e.preventDefault();
    const firstInferenceIndex = values.blocks.findIndex((b) => b.blockType === WORKFLOW_BLOCK_TYPE.INFERENCE);
    const newBlocks = [
      ...values.blocks.slice(0, firstInferenceIndex),
      { id: `cost-controls-1`, blockType: WORKFLOW_BLOCK_TYPE.COST_CONTROLS, options: { free: false } },
      ...values.blocks.slice(firstInferenceIndex),
    ];
    setFieldValue("blocks", newBlocks);
  };

  const addFeatureFlags = (e) => {
    e.preventDefault();
    const newBlocks = [{ id: `feature-flags-1`, blockType: WORKFLOW_BLOCK_TYPE.FEATURE_FLAGS, options: { subscriptionFeatureFlags: [] } }, ...values.blocks];
    setFieldValue("blocks", newBlocks);
  };

  const addInputDocument = (e) => {
    e.preventDefault();
    const apiInputIndex = values.blocks.findIndex((b) => b.blockType === WORKFLOW_BLOCK_TYPE.API_INPUT);
    const newBlocks = [...values.blocks.slice(0, apiInputIndex + 1), { id: `input-document-1`, blockType: WORKFLOW_BLOCK_TYPE.INPUT_DOCUMENT, options: {} }, ...values.blocks.slice(apiInputIndex + 1)];
    setFieldValue("blocks", newBlocks);
  };

  const addPOVAndTense = (e) => {
    e.preventDefault();
    const firstInferenceIndex = values.blocks.findIndex((b) => b.blockType === WORKFLOW_BLOCK_TYPE.INFERENCE);
    const newBlocks = [
      ...values.blocks.slice(0, firstInferenceIndex),
      { id: `pov-and-tense-1`, blockType: WORKFLOW_BLOCK_TYPE.POV_AND_TENSE, options: {} },
      ...values.blocks.slice(firstInferenceIndex),
    ];
    setFieldValue("blocks", newBlocks);
  };

  const hasCostControls = values.blocks.some((b) => b.blockType === WORKFLOW_BLOCK_TYPE.COST_CONTROLS);
  const hasInputDocument = values.blocks.some((b) => b.blockType === WORKFLOW_BLOCK_TYPE.INPUT_DOCUMENT);

  const toggleJsonModal = (e) => {
    e.preventDefault();
    if (!isJsonModalOpen) {
      // When opening, populate with current blocks JSON
      setJsonText(JSON.stringify(values.blocks, null, 2));
    }
    setIsJsonModalOpen(!isJsonModalOpen);
  };

  const handleJsonSave = () => {
    try {
      const parsedBlocks = JSON.parse(jsonText);
      setFieldValue("blocks", parsedBlocks);
      setIsJsonModalOpen(false);
    } catch (error) {
      alert("Invalid JSON format. Please check your syntax and try again.");
    }
  };

  const handleJsonCancel = () => {
    setIsJsonModalOpen(false);
    setJsonText("");
  };

  return (
    <div className="flex flex-col gap-2 mt-3">
      <div>
        <SudoButton
          variant="tertiary"
          className="mt-0 w-fit"
          onClick={enableStoryBible}
          disabled={hasStoryBible}
        >
          Enable Story Bible
        </SudoButton>
        <SudoButton
          variant="tertiary"
          className="mt-0 w-fit"
          onClick={addFeatureFlags}
          disabled={hasFeatureFlags}
        >
          Add Feature Flags
        </SudoButton>
        <SudoButton
          variant="tertiary"
          className="mt-0 w-fit"
          onClick={addAbTest}
        >
          Add A/B Test
        </SudoButton>
        <SudoButton
          variant="tertiary"
          className="mt-0 w-fit"
          onClick={addCostControls}
          disabled={hasCostControls}
        >
          Add Cost Controls
        </SudoButton>
        <SudoButton
          variant="tertiary"
          className="mt-0 w-fit"
          onClick={addInputDocument}
          disabled={hasInputDocument}
        >
          Add Input Document
        </SudoButton>
        <SudoButton
          variant="tertiary"
          className="mt-0 w-fit"
          onClick={addPOVAndTense}
          disabled={hasPOVAndTense}
        >
          Add POV and Tense
        </SudoButton>
        <SudoButton
          variant="tertiary"
          className="mt-0 w-fit"
          onClick={toggleJsonModal}
        >
          JSON Controls
        </SudoButton>
      </div>
      <div className="flex flex-col gap-2">
        {inputBlocks.map((block, index) => {
          return (
            <BlockEditor
              key={block.id}
              block={block}
              index={index}
              workflow={workflow}
            />
          );
        })}
      </div>

      <div className="flex flex-col gap-2">
        {inferenceBlocks.map((block, index) => {
          return (
            <BlockEditor
              key={block.id}
              block={block}
              index={index}
              workflow={workflow}
            />
          );
        })}
      </div>
      <div>
        <SudoButton
          variant="tertiary"
          className="mt-0 w-fit"
          onClick={addPromptStage}
        >
          + Prompt Stage
        </SudoButton>
      </div>

      <OutputEditor outputBlocks={outputBlocks} />

      <GeneralBaseModal
        isOpen={isJsonModalOpen}
        onClose={handleJsonCancel}
        title={{
          main: "JSON Controls",
          sub: "Edit the JSON directly - DANGER ZONE",
        }}
        footer={
          <div className="flex gap-2 justify-end">
            <SudoButton
              variant="secondary"
              onClick={handleJsonCancel}
            >
              Cancel
            </SudoButton>
            <SudoButton
              variant="primary"
              onClick={handleJsonSave}
            >
              Apply Changes
            </SudoButton>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600 text-left">This is used to copy and paste complete workflows between production and local development.</p>
          <textarea
            value={jsonText}
            onChange={(e) => setJsonText(e.target.value)}
            className="w-full h-96 p-3 border border-gray-300 rounded font-mono text-sm"
            placeholder="JSON will appear here..."
          />
        </div>
      </GeneralBaseModal>
    </div>
  );
}
